// @flow
import AttributeModel from "./AttributeModel";

import ChoiceAttributeOptionCollection from "./ChoiceAttributeOptionCollection";
import ContentConfigurationElements from "../contentconfiguration/ContentConfigurationElements";
import ChoiceAttributeOptionModel from "./ChoiceAttributeOptionModel";

import { RENDER_SECTION_LABEL } from "../../constants/LayoutHints";
import { ATTRIBUTE_WIDTH } from "../../constants";

import type { AttributeType, ModelOptions } from "../types";

/**
 * Model for a boolean attribute
 */
export default class BooleanAttributeModel extends AttributeModel {
  _referenceDate: string;
  _options: ChoiceAttributeOptionCollection;
  _hasContentConfiguration: boolean = false;

  /**
   */
  constructor(
    attribute: Object,
    attributeContributions: Object,
    modelOptions?: ModelOptions,
  ) {
    super(attribute, attributeContributions, modelOptions);

    this._referenceDate = this.getData(
      "referenceDate",
      new Date().toISOString().split("T")[0],
    );
    this._options = ChoiceAttributeOptionCollection.create(
      attribute,
      attributeContributions,
      { ...modelOptions, referenceDate: this.referenceDate },
    );
  }

  /**
   */
  static isApplicableModel(contributions: Object): boolean {
    return contributions.type === "boolean";
  }

  /**
   */
  get type(): string {
    return "boolean";
  }

  /**
   * Retrieve reference date of attribute which can be used as entryDate for content
   */
  get referenceDate(): string {
    return this._referenceDate;
  }

  /**
   * Set reference date for concepts and content
   */
  set referenceDate(date: string) {
    this._referenceDate = date;

    this.options.referenceDate = date;
  }

  /**
   * Getting the type of choice filter. For instance checkbox, radiobutton, combobox.
   */
  get choicetype(): "checkbox" | "radiobutton" | "combobox" | "toggle" {
    return (
      ["checkbox", "radiobutton", "combobox", "toggle"].find((hint) =>
        this.layouthint.has(hint),
      ) || "radiobutton"
    );
  }

  /**
   * Can multiple options be selected
   */
  get isMultiple(): boolean {
    return false;
  }

  /**
   * Check if options need to be rendered as tree
   */
  get isTree(): boolean {
    return false;
  }

  /**
   */
  set hasContentConfiguration(hasContentConfiguration: boolean) {
    this._hasContentConfiguration =
      this.contentConfiguration !== null || hasContentConfiguration;
  }

  /**
   */
  get hasContentConfiguration(): boolean {
    return this._hasContentConfiguration;
  }

  /**
   * Get content configuration configured on the attribute.
   * Only applicable for taxonomy element and knowledge codemaps,
   * content configuration for instrument questions is available on the form object
   */
  get contentConfiguration(): ContentConfigurationElements | null {
    if (
      !this.contributions.content ||
      !this.contributions.content.optionElements
    ) {
      return null;
    }

    // Add RENDER_SECTION_LABEL to all content configuration to make sure the label of a section is always rendered on taxonomy and knowlege codemaps
    const optionElementConfig = this.contributions.content.optionElements.map(
      (optionElement) => {
        if ("contentElement" in optionElement) {
          return {
            contentElement: {
              ...optionElement.contentElement,
              layouthint: [RENDER_SECTION_LABEL],
            },
          };
        }

        return optionElement;
      },
    );

    return new ContentConfigurationElements(optionElementConfig);
  }

  /**
   * Retrieve available choice options
   */
  get options(): ChoiceAttributeOptionCollection {
    return this._options;
  }

  /**
   * Getting all enabled options
   */
  get selected(): Array<string> {
    // $FlowFixMe[incompatible-call]
    return this.options.selected.map<string, ChoiceAttributeOptionModel>(
      (option: ChoiceAttributeOptionModel) => option.code,
    );
  }

  /**
   * Flatten options and filter out the options that are not selected
   */
  get readonlyvalue(): string {
    return this.options.selected.map((option) => option.label).join(", ");
  }

  /**
   */
  get initvalue(): any {
    if (Array.isArray(this._initvalue) && this._initvalue.length === 0) {
      return null;
    }

    return this._initvalue;
  }

  /**
   * Retrieve list of selected options, joined with comma
   */
  getValue(): string | null {
    return this.options.selected.length > 0
      ? this.options.selected.map((option) => option.code).join(",")
      : null;
  }

  /**
   */
  hasValue(): boolean {
    return this.options.selected.length > 0;
  }

  /**
   * Get input value of attribute
   */
  getInputValue(): string {
    return this.selected.join(",");
  }

  /**
   * Setting an option selected or unselected based on the current state
   */
  toggleOption(code: string) {
    if (this.selected.includes(code)) {
      this.disableOption(code);
    } else {
      this.enableOption(code);
    }
  }

  /**
   * Enable a option
   */
  enableOption(code: string) {
    this.options.deselectAll();
    this.options.select(code);
    this.inputvalue = this.getInputValue();
  }

  /**
   * Disable a option
   */
  disableOption(code: string) {
    this.options.deselect(code);
    this.inputvalue = this.getInputValue();
  }

  /**
   * Reset boolean attribute to unselected list
   */
  reset() {
    this.options.deselectAll();
  }

  /**
   */
  mergeAttribute(oldAttribute: AttributeType) {
    // when attribute is readonly, don't merge the options no modifications necessary
    if (!this.readonly && oldAttribute instanceof BooleanAttributeModel) {
      this.concept = oldAttribute.concept;
      this.options.deselectAll();

      if (oldAttribute.isValid && oldAttribute.inputvalue !== null) {
        this.update(oldAttribute.inputvalue);
      }
    }
  }

  /**
   * Update attribute by name and value
   */
  update(value: any): BooleanAttributeModel {
    if (this.readonly) {
      return this;
    }

    this.updateLastModification();

    let values = [];

    if (Array.isArray(value)) {
      values = value;
    } else if (typeof value === "string" && this.isMultiple) {
      values = value.split(",");
    } else if (value != null) {
      values = [value.toString()];
    }

    values.forEach((val) => {
      if (val instanceof ChoiceAttributeOptionModel) {
        this.toggleOption(val.code);
      } else if (
        typeof val === "string" &&
        this.options.some((option) => option.code.toString() === val)
      ) {
        this.toggleOption(val);
      }
    });

    return this;
  }

  /**
   */
  updateByAttribute(otherAttribute: AttributeType): void {
    this.reset();
    if (otherAttribute instanceof BooleanAttributeModel) {
      this.update(otherAttribute.value);
    } else {
      console.error(
        `update of boolean attribute with ${otherAttribute.type} not supported`,
      );
    }
  }

  /**
   */
  getFormData(): { [string]: boolean | null } | null {
    if (this.inError()) {
      return null;
    }

    if (!this.hasValue()) {
      // no value for checkbox and toggle is same as a false value
      if (this.choicetype === "checkbox" || this.choicetype === "toggle") {
        return { [this.name]: false };
      }

      return { [this.name]: null };
    }

    return { [this.name]: this.value === "true" };
  }

  /**
   */
  get readonlyWidth(): $Keys<typeof ATTRIBUTE_WIDTH> {
    if (this.options.some((option) => option.hasAlternativeLabel)) {
      return ATTRIBUTE_WIDTH.SMALL;
    }

    return ATTRIBUTE_WIDTH.EXTRA_SMALL;
  }
}
